// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Account } from '../../cache/Account';
import { AccountFeature } from '@shared/utils/Account/AccountFeature';
import { Resources } from '@shared/localizations/Resources';
import { AccountSummaryTablesEnum } from '../Columns/AccountSummaryTablesEnum';
import { TvAccountColumns } from '../Columns/TvAccountColumns';
import { TvAccountHelper } from '../Helpers/TvAccountHelper';
import { TvAccountActivityData } from '../TradingViewPrimitives/AccountData/TvAccountActivityData';
import { TvAccountGeneralData } from '../TradingViewPrimitives/AccountData/TvAccountGeneralData';
import { TvAccountMarginData } from '../TradingViewPrimitives/AccountData/TvAccountMarginData';
import { TvAccountTodaysResultData } from '../TradingViewPrimitives/AccountData/TvAccountTodaysResultData';
import { GetCustomFormatters } from '../Columns/CustomTableFormatters/BaseCustomTableFormatterName';
import { TvRiskManagementData } from '../TradingViewPrimitives/AccountData/TvRiskManagementData';
export class TvAccountTableManager {
    constructor(host) {
        this.needUpdateEquity = false;
        this.needUpdateMarginAvailable = false;
        this.prevEquity = 0;
        this.prevMarginAvailable = 0;
        this.getDataDelegate = (tableType) => {
            switch (tableType) {
                case AccountSummaryTablesEnum.General:
                    return this.getGeneralData;
                case AccountSummaryTablesEnum.Margin:
                    return this.getMarginData;
                case AccountSummaryTablesEnum.AccountActivity:
                    return this.getActivityData;
                case AccountSummaryTablesEnum.TodaysResults:
                    return this.getTodayResultsData;
                default:
                    throw new Error('Invalid Table Type');
            }
        };
        this.getGeneralData = (paginationLastId) => __awaiter(this, void 0, void 0, function* () {
            return yield Promise.resolve([this.generalData]);
        });
        this.getMarginData = (paginationLastId) => __awaiter(this, void 0, void 0, function* () {
            return yield Promise.resolve([this.marginData]);
        });
        this.getActivityData = (paginationLastId) => __awaiter(this, void 0, void 0, function* () {
            return yield Promise.resolve([this.activityData]);
        });
        this.getTodayResultsData = (paginationLastId) => __awaiter(this, void 0, void 0, function* () {
            return yield Promise.resolve([this.todaysResultsData]);
        });
        this.host = host;
        this.generalData = new TvAccountGeneralData();
        this.marginData = new TvAccountMarginData();
        this.activityData = new TvAccountActivityData();
        this.todaysResultsData = new TvAccountTodaysResultData();
        this.riskManagementData = new TvRiskManagementData();
        this.generalDelegate = host.factory.createDelegate();
        this.marginDelegate = host.factory.createDelegate();
        this.activityDelegate = host.factory.createDelegate();
        this.todaysResultsDelegate = host.factory.createDelegate();
        this.riskManagementDelegate = host.factory.createDelegate();
        this.balanceValue = host.factory.createWatchedValue(this.generalData.balance);
        this.projectedBalanceValue = host.factory.createWatchedValue(this.generalData.projectedBalance);
        this.openGrossPLValue = host.factory.createWatchedValue(this.activityData.openGrossPL);
        this.openNetPLValue = host.factory.createWatchedValue(this.activityData.openNetPL);
        this.todayOpenGrossPLValue = host.factory.createWatchedValue(this.todaysResultsData.todaysGross);
        this.todayOpenNetPLValue = host.factory.createWatchedValue(this.todaysResultsData.todaysNet);
        this.currentDailyLossValue = host.factory.createWatchedValue(this.riskManagementData.currentDailyLoss);
        this.maxDrawdownLevelValue = host.factory.createWatchedValue(this.riskManagementData.maxDrawdownLevel);
        this.generalDelegate.subscribe(null, (t) => {
            this.balanceValue.setValue(t.balance);
            this.projectedBalanceValue.setValue(t.projectedBalance);
        });
        this.activityDelegate.subscribe(null, (t) => {
            this.openGrossPLValue.setValue(t.openGrossPL);
            this.openNetPLValue.setValue(t.openNetPL);
        });
        this.todaysResultsDelegate.subscribe(null, (t) => {
            this.todayOpenGrossPLValue.setValue(t.todaysGross);
            this.todayOpenNetPLValue.setValue(t.todaysNet);
        });
        this.riskManagementDelegate.subscribe(null, (t) => {
            this.currentDailyLossValue.setValue(t.currentDailyLoss);
            this.maxDrawdownLevelValue.setValue(t.maxDrawdownLevel);
        });
    }
    subscribeEquity() {
        this.needUpdateEquity = true;
    }
    unsubscribeEquity() {
        this.needUpdateEquity = false;
    }
    subscribeMarginAvailable(symbol) {
        this.needUpdateMarginAvailable = true;
    }
    unsubscribeMarginAvailable(symbol) {
        this.needUpdateMarginAvailable = false;
    }
    updateTable() {
        this.updateTables();
        if (this.needUpdateEquity) {
            const acc = TvAccountHelper.getCurrentAccount();
            const balance = Account.GetAccountFeature(AccountFeature.Balance, acc, acc.assetBalanceDefault);
            const pnl = acc.Profit;
            const equity = balance + pnl;
            if (equity !== this.prevEquity)
                this.host.equityUpdate(equity);
            this.prevEquity = equity;
        }
        if (this.needUpdateMarginAvailable) {
            const acc = TvAccountHelper.getCurrentAccount();
            const marginAvailable = Account.GetAccountFeature(AccountFeature.MarginAvailable, acc, acc.assetBalanceDefault);
            if (marginAvailable !== this.prevMarginAvailable)
                this.host.marginAvailableUpdate(marginAvailable);
            this.prevMarginAvailable = marginAvailable;
        }
    }
    updateTables() {
        const acc = TvAccountHelper.getCurrentAccount();
        if (this.generalData.update(acc)) {
            this.generalDelegate.fire(this.generalData);
        }
        if (this.marginData.update(acc)) {
            this.marginDelegate.fire(this.marginData);
        }
        if (this.activityData.update(acc)) {
            this.activityDelegate.fire(this.activityData);
        }
        if (this.todaysResultsData.update(acc)) {
            this.todaysResultsDelegate.fire(this.todaysResultsData);
        }
        if (this.riskManagementData.update(acc)) {
            this.riskManagementDelegate.fire(this.riskManagementData);
        }
    }
    getSummary() {
        const summaryItems = [
            { resourceKey: 'panel.accounts.Balance', value: this.balanceValue },
            { resourceKey: 'panel.accounts.CurBalance', value: this.projectedBalanceValue },
            { resourceKey: 'panel.accounts.Profit', value: this.openGrossPLValue },
            { resourceKey: 'panel.accounts.ProfitNet', value: this.openNetPLValue },
            { resourceKey: 'panel.accounts.GrossPNL', value: this.todayOpenGrossPLValue },
            { resourceKey: 'panel.accounts.BeginBalance', value: this.todayOpenNetPLValue },
            { resourceKey: 'panel.accounts.CurrentDailyLoss', value: this.currentDailyLossValue },
            { resourceKey: 'panel.accounts.MaxDrawdownLevel', value: this.maxDrawdownLevelValue }
        ];
        return summaryItems.map((item) => this.createSummaryItem(Resources.getResource(item.resourceKey), item.value));
    }
    getPages() {
        const tables = this.getTables();
        if (tables.length === 0) {
            return [];
        }
        return [
            {
                id: 'accountsummary',
                title: Resources.getResource('TvTradingPlatform.AccountSummary.Title'),
                tables
            }
        ];
    }
    getTables() {
        const generalTable = this.createTableItem(AccountSummaryTablesEnum.General);
        const marginTable = this.createTableItem(AccountSummaryTablesEnum.Margin);
        const accountActivityTable = this.createTableItem(AccountSummaryTablesEnum.AccountActivity);
        const todaysResultsTable = this.createTableItem(AccountSummaryTablesEnum.TodaysResults);
        const tables = [generalTable, marginTable, accountActivityTable, todaysResultsTable];
        return tables.filter((t) => t !== null);
    }
    createTableItem(tableType) {
        const columns = TvAccountColumns.getColumns(tableType);
        if (columns.length === 0) {
            return null;
        }
        const changeDelegate = this.getChangeDelegate(tableType);
        const table = {
            id: tableType.toString(),
            title: this.getTableTitle(tableType),
            columns: TvAccountColumns.getColumns(tableType),
            getData: this.getDataDelegate(tableType),
            changeDelegate
        };
        return table;
    }
    getCustomFormatters() {
        const formatters = GetCustomFormatters();
        return formatters;
    }
    getChangeDelegate(tableType) {
        switch (tableType) {
            case AccountSummaryTablesEnum.General:
                return this.generalDelegate;
            case AccountSummaryTablesEnum.Margin:
                return this.marginDelegate;
            case AccountSummaryTablesEnum.AccountActivity:
                return this.activityDelegate;
            case AccountSummaryTablesEnum.TodaysResults:
                return this.todaysResultsDelegate;
            default:
                return this.generalDelegate;
        }
    }
    createSummaryItem(name, wValue, formatter = "fixed" /* StandardFormatterName.Fixed */, isDefault = true) {
        const summaryItem = {
            text: name,
            wValue,
            formatter,
            isDefault
        };
        return summaryItem;
    }
    getTableTitle(tableType) {
        switch (tableType) {
            case AccountSummaryTablesEnum.General:
                return Resources.getResource('panel.accountDetails.Groups.1.General');
            case AccountSummaryTablesEnum.Margin:
                return Resources.getResource('panel.accountDetails.Groups.2.Margin');
            case AccountSummaryTablesEnum.AccountActivity:
                return Resources.getResource('panel.accountDetails.Groups.3.AccountActivity');
            case AccountSummaryTablesEnum.TodaysResults:
                return Resources.getResource('panel.accountDetails.Groups.4.TodayResults');
            default:
                throw new Error('Invalid Table Type');
        }
    }
}
